import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import AssetHeaderCard from '../components/advanced_asset/AssetHeaderCard';
import RichViewToggleButton from '../components/advanced_asset/RichViewToggleButton';
import LoadingView from '../../shared/components/LoadingView';
import ErrorView from '../components/errors/ErrorView';
import ChargingSocketsCard from '../components/advanced_asset/ChargingSocketsCard';
import SystemStatusCard from '../components/advanced_asset/SystemStatusCard';
import ActiveAlertsCard from '../components/advanced_asset/ActiveAlertsCard';
import QuickLinksCard from '../components/advanced_asset/QuickLinksCard';
import MapLibreMap from '../components/MapLibreMap';
import ComponentStatusAccordion from '../components/advanced_asset/ComponentStatusAccordion';
import { getIntegrationManifest } from '../../integrations';
import { API_URL } from '../../config/env';

const KEMPOWER_QUICK_LINKS = [
  {
    label: 'MyKempower Portal',
    subLabel: 'Requires Access',
    url: 'https://dev.mykempower.io/',
    external: true,
  },
];

function buildConnectors(components) {
  return (components || [])
    .filter((component) => component.component_type === 'connector')
    .map((component) => ({
      name: component.name,
      type: component.metadata?.type,
      powerKw: component.metadata?.power_kw,
      status: component.status,
    }));
}

export default function AdvancedAssetUser() {
  const [diagnostics, setDiagnostics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { access_key } = useParams();

  const fetchAdvancedAsset = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = `${API_URL}/v1/assets/access_key/${access_key}/advanced`;
      const res = await fetch(url);
      if (!res.ok) {
        throw new Error(`Fetch error: ${res.status} ${res.statusText}`);
      }
      const json = await res.json();
      setDiagnostics(json);
      setLoading(false);
    } catch (err) {
      setError(err);
      setLoading(false);
    }
  }, [access_key]);

  useEffect(() => {
    fetchAdvancedAsset();
  }, [fetchAdvancedAsset]);

  const deviceComponents = useMemo(
    () => (diagnostics?.components || []).filter((component) => component.component_type !== 'connector'),
    [diagnostics]
  );

  const connectors = useMemo(
    () => buildConnectors(diagnostics?.components),
    [diagnostics]
  );

  const quickLinks = useMemo(() => {
    const manifest = getIntegrationManifest(diagnostics?.integration_type);
    if (manifest?.type === 'Integrations::KempowerChargEyeIntegration') {
      return KEMPOWER_QUICK_LINKS;
    }
    return [];
  }, [diagnostics?.integration_type]);

  if (loading) {
    return (
      <LoadingView
        title="Loading Asset Details"
        subtitle="Please wait while we fetch the latest information"
        extra="Connecting to server..."
        fullViewport
      />
    );
  }

  if (error) {
    return (
      <ErrorView
        title="Connection Lost"
        message="We couldn't load the latest data from the backend."
        onRetry={fetchAdvancedAsset}
        onGoBack={() => window.history.back()}
      />
    );
  }

  if (!diagnostics) {
    return (
      <div className="text-center mt-5">
        No asset diagnostics found.
      </div>
    );
  }

  const { asset, conditions, fetched_at, error: backendError } = diagnostics;

  if (backendError) {
    return (
      <div className="text-center mt-5 text-danger">
        Backend error: {backendError}
      </div>
    );
  }

  const stationName = asset?.name || 'Unnamed Asset';
  const onlineStatus = asset?.online_status || 'offline';
  const locationStr = asset?.location?.address || 'No address';
  const lat = asset?.location?.lat;
  const lng = asset?.location?.lng;
  const firmwareVersion = asset?.firmware_version || 'Unknown FW';
  const operationalStatus = asset?.operational_status || 'unknown';
  const activeAlerts = conditions || [];
  const lastUpdated = fetched_at ? new Date(fetched_at).toLocaleTimeString() : 'Unknown';

  function handleFirmwareUpdate() {
    alert('Firmware update requested...');
  }

  return (
    <div className="container mt-3">
      <AssetHeaderCard
        stationName={stationName}
        lastUpdated={lastUpdated}
        location={locationStr}
        onlineStatus={onlineStatus}
      />

      <RichViewToggleButton active hasIntegration />

      <div className="row">
        <div className="col-12 mb-3">
          <MapLibreMap lat={lat} lng={lng} />
        </div>
      </div>

      <ChargingSocketsCard connectors={connectors} />

      <SystemStatusCard
        operationalStatus={operationalStatus}
        firmwareVersion={firmwareVersion}
        onFirmwareUpdate={handleFirmwareUpdate}
      />

      <ActiveAlertsCard alerts={activeAlerts} />

      <ComponentStatusAccordion components={deviceComponents} />

      {quickLinks.length > 0 && <QuickLinksCard links={quickLinks} />}
    </div>
  );
}
